/**
 * Attorney-facing wording for a file's processing status. The stored values are
 * engineering vocabulary, and inaccurate at that — `textract_failed` is recorded
 * for a DOCX that never reached Textract.
 */

import type { FileProcessingStatus } from "@patent-studio/api-types";

const LABELS: Record<FileProcessingStatus, string> = {
  uploaded: "Waiting to be read",
  textract_pending: "Waiting to be read",
  textract_processing: "Reading…",
  textract_complete: "Read",
  indexing: "Reading…",
  indexed: "Ready",
  // Same sentence for both: which stage failed is ours to act on, not the
  // attorney's, and naming it invites "what is indexing?".
  textract_failed: "Couldn't read this file",
  indexing_failed: "Couldn't read this file",
  // "Prepared" rather than "rendered": rendering is ours, not the attorney's.
  render_pending: "Waiting to be prepared",
  render_processing: "Preparing the pages…",
  render_complete: "Ready",
  render_failed: "Couldn't prepare this file",
  // Audio clips live on the Define card, which has wording of its own; these
  // exist so a clip that ever surfaces in a file list still reads as English.
  transcribe_pending: "Waiting to be transcribed",
  transcribe_processing: "Transcribing…",
  transcribe_complete: "Transcribed",
  transcribe_failed: "Couldn't transcribe this clip",
};

export function fileStatusLabel(status: FileProcessingStatus): string {
  return LABELS[status];
}

/**
 * The statuses a PDF of drawings passes through. Listed rather than matched by
 * name, since a prefix rule would take in whatever a later status is called.
 */
const DRAWN_INTO_FIGURES: ReadonlySet<FileProcessingStatus> = new Set([
  "render_pending",
  "render_processing",
  "render_complete",
  "render_failed",
]);

/**
 * Whether the pages of a supplied PDF are not on the task yet: waiting, drawn,
 * or refused. False once they land, since they are figures then.
 */
export function isAwaitingPages(status: FileProcessingStatus): boolean {
  return DRAWN_INTO_FIGURES.has(status) && status !== "render_complete";
}

/** Whether a status means the file still has work outstanding. */
export function isFileSettled(status: FileProcessingStatus): boolean {
  return (
    status === "indexed" ||
    status === "render_complete" ||
    status.endsWith("_failed")
  );
}

export const RETRY_LABEL = "Try again";
export const RETRY_PENDING_LABEL = "Trying again…";

/**
 * The retry control's accessible name. Named after the file because a
 * component can hold several, and identical "Try again" buttons say nothing
 * about which file each one moves.
 */
export function retryActionLabel(filename: string): string {
  return `Try again with ${filename}`;
}
